/**
 * Session Manager for Analytics
 * Generates and manages session IDs for tracking user sessions
 */

const SESSION_STORAGE_KEY = 'analytics_session_id';
const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes
const SESSION_TIMESTAMP_KEY = `${SESSION_STORAGE_KEY}_timestamp`;

const createSessionId = (): string => {
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    sessionStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
    return sessionId;
};

const getSessionTimestamp = (): number | null => {
    const raw = sessionStorage.getItem(SESSION_TIMESTAMP_KEY);
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
};

export const getOrCreateSessionId = (): string => {
    let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const sessionTimestamp = getSessionTimestamp();

    // Check if session exists and is still valid
    if (sessionId && sessionTimestamp) {
        const now = Date.now();
        
        // If session is less than 30 minutes old, reuse it
        if (now - sessionTimestamp < SESSION_DURATION) {
            return sessionId;
        }
    }

    // Create new session
    sessionId = createSessionId();

    return sessionId;
};

export const touchCurrentSession = (): void => {
    if (!sessionStorage.getItem(SESSION_STORAGE_KEY)) {
        return;
    }
    sessionStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
};

export const ensureActiveSessionId = (): string => {
    const existingId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const timestamp = getSessionTimestamp();
    const now = Date.now();

    if (!existingId || !timestamp) {
        return createSessionId();
    }

    if (now - timestamp >= SESSION_DURATION) {
        return createSessionId();
    }

    touchCurrentSession();
    return existingId;
};

export const clearSession = (): void => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_TIMESTAMP_KEY);
};

export const getCurrentSessionId = (): string | null => {
    return sessionStorage.getItem(SESSION_STORAGE_KEY);
};
